// Copyright (c) 2026 Celadon Development LLC, All rights reserved.
// Author Vasily Klyuev <vasily.klyuev@celadon.ae>
// Redeem a voucher at the till: enter or scan the code, check what is on it,
// then take an amount off.
//
// Replaces the old combined-balance redemption, which spent a pooled
// tblBookshopCustomers.Balance. Vouchers now carry their own balance and are
// redeemed one at a time, and only at the shop that issued them.
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';
import { ArrowBackIos } from '@material-ui/icons';
import { Link } from 'react-router-dom';

import { Button, ConfirmModal, Loader } from '../../../components';
import history from '../../../services/history';
import * as redeemActions from '../store/actions';
import {
  getLoader,
  getCode,
  getAmount,
  getVoucher,
  getMessage,
} from '../store/selectors';

import { isRedeemableAmount, cleanAmount } from '../../../services/voucherAmount';

import styles from './styles';

class RedeemVoucherPage extends Component {
  state = {
    confirming: false,
  };

  static propTypes = {
    classes: PropTypes.object,
    location: PropTypes.object,
    loading: PropTypes.bool,
    code: PropTypes.string,
    amount: PropTypes.string,
    voucher: PropTypes.object,
    message: PropTypes.object,
    changeCode: PropTypes.func,
    changeAmount: PropTypes.func,
    scanVoucher: PropTypes.func,
    redeemVoucher: PropTypes.func,
    reset: PropTypes.func,
  };

  static defaultProps = {
    classes: {},
    location: null,
    loading: false,
    code: '',
    amount: '',
    voucher: null,
    message: null,
    changeCode: () => { },
    changeAmount: () => { },
    scanVoucher: () => { },
    redeemVoucher: () => { },
    reset: () => { },
  };

  componentDidMount() {
    const code = this.props.location
      && this.props.location.state
      && this.props.location.state.code;

    if (code) {
      this.props.changeCode(code);
      this.props.scanVoucher();
    }
  }

  componentWillUnmount() {
    // Never leave one customer's voucher on screen for the next one.
    this.props.reset();
  }

  // A handheld scanner types the code and sends Enter, so submitting on Enter
  // is what makes the scanner work at all. There is no camera in the browser.
  // keydown, not keypress - keypress is deprecated and skipped for some keys.
  onCodeKeyDown = (event) => {
    if (event.key === 'Enter') {
      this.props.scanVoucher();
    }
  }

  onAmountKeyDown = (event) => {
    if (event.key === 'Enter') {
      this.askToRedeem();
    }
  }

  /**
   * Nothing is deducted until this is confirmed.
   *
   * A voucher redemption cannot be undone from this screen, and the amount is
   * typed by hand at a till - a slipped decimal point takes £27.60 off a
   * customer's voucher instead of £2.76. The operator sees the figure and what
   * would be left before it happens.
   *
   * An amount that is not usable at all skips the dialog and goes straight to
   * the saga, which owns the wording of every error on this screen - there is
   * no sense asking someone to confirm "take £NaN off this voucher".
   */
  askToRedeem = () => {
    const { amount, voucher } = this.props;

    if (!voucher || !isRedeemableAmount(amount, voucher.Balance)) {
      this.props.redeemVoucher();
      return;
    }

    this.setState({ confirming: true });
  }

  closeConfirm = () => this.setState({ confirming: false })

  confirmRedeem = () => {
    this.setState({ confirming: false });
    this.props.redeemVoucher();
  }

  /** e.g. "Take £2.76 off voucher SIB4K7M2P9Q? £7.24 would be left." */
  confirmQuestion = () => {
    const { amount, voucher } = this.props;

    if (!voucher) {
      return '';
    }

    const taking = parseFloat(cleanAmount(amount));
    const left = parseFloat(voucher.Balance) - taking;

    return `Take £${taking.toFixed(2)} off voucher ${voucher.Code}? `
      + `£${left.toFixed(2)} would be left on it.`;
  }

  bookshopCustomerId = () => {
    const { voucher, location } = this.props;

    if (voucher && voucher.BookshopCustomerId) {
      return voucher.BookshopCustomerId;
    }

    const arrivedWith = location && location.state;

    if (arrivedWith && voucher && arrivedWith.code === voucher.Code) {
      return arrivedWith.bookshopCustomerId || null;
    }

    return null;
  }

  renderVoucher = () => {
    const { classes, voucher, amount } = this.props;
    const bookshopCustomerId = this.bookshopCustomerId();

    const balance = parseFloat(voucher.Balance);
    const typed = parseFloat(amount);
    // What the customer still owes on this sale once the voucher is applied.
    // Only meaningful while a valid amount is typed.
    const showsRemainder = !Number.isNaN(typed) && typed > 0 && typed <= balance;

    return (
      <div className={classes.voucherPanel}>
        <div className={classes.detailRow}>
          <p className={classes.detailLabel}>Balance</p>
          <p className={classes.balanceValue}>{`£${balance.toFixed(2)}`}</p>
        </div>
        <div className={classes.detailRow}>
          <p className={classes.detailLabel}>Code</p>
          <p className={classes.detailValue}>{voucher.Code}</p>
        </div>
        {!!voucher.CustomerName && (
          <div className={classes.detailRow}>
            <p className={classes.detailLabel}>Customer</p>
            <p className={classes.detailValue}>{voucher.CustomerName}</p>
          </div>
        )}
        <div className={classes.detailRow}>
          <p className={classes.detailLabel}>Expires</p>
          <p className={classes.detailValue}>
            {voucher.ExpiryDate ? moment(voucher.ExpiryDate).format('DD/MM/YYYY') : '-'}
          </p>
        </div>
        <div className={classes.detailRow}>
          <p className={classes.detailLabel}>Status</p>
          <p className={classes.detailValue}>{voucher.Status}</p>
        </div>
        {showsRemainder && (
          <div className={classes.detailRow}>
            <p className={classes.detailLabel}>Left on voucher after this</p>
            <p className={classes.detailValue}>{`£${(balance - typed).toFixed(2)}`}</p>
          </div>
        )}
        {!!bookshopCustomerId && (
          <Link
            className={classes.historyLink}
            to={{
              pathname: `/account/vouchers/customer-overview/${bookshopCustomerId}`,
              state: { bookshopCustomerId },
            }}
          >
            {'View issue and redemption history >'}
          </Link>
        )}
      </div>
    );
  }

  render() {
    const {
      classes,
      loading,
      code,
      amount,
      voucher,
      message,
      changeCode,
      changeAmount,
      scanVoucher,
    } = this.props;

    const canRedeem = !!voucher && voucher.IsRedeemable;

    return (
      <div className={classes.root}>
        <div
          role={'button'}
          tabIndex={0}
          onKeyPress={() => history.goBack()}
          onClick={() => history.goBack()}
          className={classes.arrowBackContainer}
        >
          <ArrowBackIos className={classes.link} />
          <div className={classes.link}>Back</div>
        </div>
        <h4 className={classes.screenTitle}>REDEEM VOUCHER</h4>
        <div className={classes.container}>
          <div className={classes.row}>
            <input
              className={classes.input}
              value={code}
              placeholder={'Scan or type the voucher code'}
              onChange={event => changeCode(event.target.value)}
              onKeyDown={this.onCodeKeyDown}
              autoFocus
            />
          </div>
          <p className={classes.help}>
            Scan the barcode with a handheld scanner, or type the code and press Enter.
          </p>

          {loading && <Loader />}

          {!!voucher && this.renderVoucher()}

          {canRedeem && (
            <React.Fragment>
              <div className={classes.row}>
                <input
                  className={classes.input}
                  value={amount}
                  placeholder={'Amount to redeem, e.g. 2.76'}
                  onChange={event => changeAmount(event.target.value)}
                  onKeyDown={this.onAmountKeyDown}
                />
              </div>
              <p className={classes.help}>
                Part of a voucher can be used - whatever is left stays on it for next time.
                Apply the same amount as a discount on your own till.
              </p>
            </React.Fragment>
          )}

          {!!message && (
            <p className={`${classes.message} ${message.error ? classes.error : classes.success}`}>
              {message.text}
            </p>
          )}

          <div className={classes.buttonWrapper}>
            {canRedeem
              ? <Button upperCase text={'redeem'} onPress={this.askToRedeem} />
              : <Button upperCase text={'find voucher'} onPress={scanVoucher} />}
          </div>

          <ConfirmModal
            modal={this.state.confirming}
            question={this.confirmQuestion()}
            closeModal={this.closeConfirm}
            onAgree={this.confirmRedeem}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  loading: getLoader(state),
  code: getCode(state),
  amount: getAmount(state),
  voucher: getVoucher(state),
  message: getMessage(state),
});

const mapDispatchToProps = dispatch => ({
  changeCode: payload => dispatch(redeemActions.changeCode(payload)),
  changeAmount: payload => dispatch(redeemActions.changeAmount(payload)),
  scanVoucher: () => dispatch(redeemActions.scanVoucher()),
  redeemVoucher: () => dispatch(redeemActions.redeemVoucher()),
  reset: () => dispatch(redeemActions.reset()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(RedeemVoucherPage));
